/**
 * The e-hentai / exhentai image-URL pipeline.
 *
 * Per page, three things need to come together to get a working `<img src>`:
 *
 *   1. **ptoken** — a per-page token, used to build `/s/{ptoken}/{gid}-{N}`.
 *      Sources of truth, in preferred order:
 *        a. IndexedDB cache (we stash the whole array on first resolve)
 *        b. The MPV (`/mpv/{gid}/{token}`) page — exposes them as a JS
 *           array, but requires the user to have the `q` H@H perk.
 *        c. Iterating preview pages (`/g/{gid}/{token}/?p=N`) and scraping
 *           the `<a href="/s/{ptoken}/{gid}-{page}">` anchors.
 *
 *   2. **showkey** — a per-gallery key, present once in `/s/{ptoken}/{gid}-1`
 *      HTML as `var showkey="...";`. Required for every subsequent
 *      `showpage` POST.
 *
 *   3. **image URL** — for page 1, extracted from `/s/.../1` directly; for
 *      pages 2..N, returned by the `showpage` API.
 *
 * Image bytes never flow through our worker; we hand the final URL to a
 * plain `<img>` tag. The browser does CORS-free image loads, but we lose
 * the ability to SHA-1-verify bytes — see the README for that trade-off.
 *
 * 509 detection: we URL-pattern-match (`Url509Regex`) before returning. If
 * the URL is the well-known "509.gif" placeholder, we throw `QuotaExceeded`.
 */

import { ParseError, QuotaExceeded } from '@/lib/errors';
import { getDb } from '@/db/schema';
import { recordDiagnostic } from '@/features/diagnostics';
import { gtoken, showpage, unescapeJsonHtml } from './api-php';
import { mpvUrl, pageUrl, withNw, galleryUrl, type SiteContext } from './urls';
import { Url509Regex } from './errors';
import { fetchEhText } from './network';
import { parseMpvPtokens } from './parsers/mpv';
import { parsePreviewPage } from './parsers/preview';

export { Url509Regex };

// EhViewer's verbatim regex patterns for the `/s/.../1` page.
export const PATTERN_IMAGE_URL = /<img id="img" [^>]*src="([^"]+)"/i;
export const PATTERN_SKIP_HATH_KEY = /onclick="return nl\('([^)]+)'\)/i;
export const PATTERN_SHOW_KEY = /var\s+showkey="([0-9a-z]+)";/i;
export const PATTERN_ORIGIN_IMAGE_URL = /<a href="([^"]+fullimg[^"]+)">/i;
/** Same regex but applied to the JSON-unescaped `i3` chunk from `showpage`. */
export const PATTERN_SHOWPAGE_IMAGE_URL = /<img[^>]+id="img"[^>]+src="([^"]+)"/i;

const PTOKENS_CACHE_PREFIX = 'eh.ptokens'; // key = `${prefix}:${siteId}:${gid}`

interface PtokenCacheRow {
  /** `[pageNumber, ptoken]` tuples. */
  entries: Array<[number, string]>;
  /** Total pages stored at write time. */
  total?: number;
  /** When this cache row was written, ms since epoch. */
  cachedAt: number;
}

function cacheKey(siteId: 'ehentai' | 'exhentai', gid: number | string): string {
  return `${PTOKENS_CACHE_PREFIX}:${siteId}:${gid}`;
}

function previewInFlightKey(
  siteId: 'ehentai' | 'exhentai',
  gid: number | string,
  previewPage: number,
): string {
  return `${siteId}:${gid}:${previewPage}`;
}

const previewPageInFlight = new Map<string, Promise<PtokenSet>>();
const ptokenMemoryCache = new Map<string, Map<number, string>>();
const ptokenMemoryPreviewSizes = new Map<string, number>();

function mergePtokenMemoryCache(
  siteId: 'ehentai' | 'exhentai',
  gid: number | string,
  entries: Array<[number, string]>,
): void {
  if (entries.length === 0) return;
  const key = cacheKey(siteId, gid);
  let cached = ptokenMemoryCache.get(key);
  if (!cached) {
    cached = new Map<number, string>();
    ptokenMemoryCache.set(key, cached);
  }
  for (const [page, token] of entries) cached.set(page, token);
}

function rememberPreviewPageSize(
  siteId: 'ehentai' | 'exhentai',
  gid: number | string,
  entries: Array<[number, string]>,
): void {
  if (entries.length === 0) return;
  ptokenMemoryPreviewSizes.set(cacheKey(siteId, gid), entries.length);
}

function ptokenCacheSummary(
  entries: Array<[number, string]>,
  total?: number,
): Record<string, unknown> {
  const pages = entries.map(([page]) => page).sort((a, b) => a - b);
  return {
    entries: entries.length,
    hasTotal: total !== undefined,
    ...(total !== undefined ? { total } : {}),
    ...(pages[0] !== undefined ? { firstPage: pages[0] } : {}),
    ...(pages[pages.length - 1] !== undefined ? { lastPage: pages[pages.length - 1] } : {}),
  };
}

async function readPtokenCache(
  siteId: 'ehentai' | 'exhentai',
  gid: number | string,
): Promise<PtokenCacheRow | undefined> {
  try {
    const db = await getDb();
    const row = await db.get('meta', cacheKey(siteId, gid));
    if (!row || typeof row.value !== 'object' || row.value === null) return undefined;
    const v = row.value as Partial<PtokenCacheRow>;
    if (!Array.isArray(v.entries)) return undefined;
    mergePtokenMemoryCache(siteId, gid, v.entries as Array<[number, string]>);
    return {
      entries: v.entries as Array<[number, string]>,
      total: v.total,
      cachedAt: v.cachedAt ?? 0,
    };
  } catch {
    return undefined;
  }
}

async function writePtokenCache(
  siteId: 'ehentai' | 'exhentai',
  gid: number | string,
  data: { entries: Array<[number, string]>; total?: number },
): Promise<void> {
  mergePtokenMemoryCache(siteId, gid, data.entries);
  try {
    const db = await getDb();
    const row: PtokenCacheRow = {
      entries: data.entries,
      cachedAt: Date.now(),
    };
    if (data.total !== undefined) row.total = data.total;
    await db.put('meta', { key: cacheKey(siteId, gid), value: row });
  } catch {
    // Cache write failures are non-fatal; ptokens are derivable on demand.
  }
}

export interface PtokenResolutionOpts {
  cookies?: string;
  /**
   * If true, the MPV-scrape fallback is skipped. EhViewer only lets MPV
   * resolution succeed when the account has the `q` H@H perk; the SPA can
   * pass `mpv: false` after a 403 to avoid retrying it for the same gallery.
   */
  mpv?: boolean;
  /**
   * How many `?p=N` preview pages to scan at most. Galleries with thousands
   * of pages have ~50 preview pages; we cap at 20 by default to keep the
   * request budget under control. Returned page count tells the caller
   * whether they got the whole array.
   */
  maxPreviewPages?: number;
  /** Hint about total pages — lets us know when we have a complete set. */
  expectedTotal?: number;
  /**
   * Skip the IDB cache (useful for force-refresh after a parser change).
   */
  bypassCache?: boolean;
}

export interface PtokenSet {
  /** `[pageNumber, ptoken]` tuples. Sorted ascending by page number. */
  entries: Array<[number, string]>;
  /** Best known gallery page count, when detail/cache/preview data exposes it. */
  total?: number;
  /** Whether we found the full set or just a prefix. */
  complete: boolean;
  /** How we obtained the tokens. */
  source: 'cache' | 'mpv' | 'preview' | 'partial-preview';
}

/**
 * Resolve every page's ptoken for a gallery, with three cascading fallbacks.
 *
 * Caching: a previously-resolved row short-circuits, but only when it is
 * authoritatively complete — i.e. the cached row carried the gallery's
 * `total` page count alongside the entries AND `entries.length >= total`.
 * That guard is the cache-layer counterpart to the preview-loop fix below;
 * without it, a partial scrape from an earlier session would masquerade as
 * complete forever.
 */
export async function getPTokensForGallery(
  gid: number | string,
  galleryToken: string,
  site: SiteContext,
  opts: PtokenResolutionOpts = {},
): Promise<PtokenSet> {
  /**
   * Caller-supplied or HTML-derived total page count. We let the preview-
   * page parse fill this in if the caller didn't provide it, because every
   * gallery-detail HTML response exposes `Length: N pages` in `#gdd`.
   */
  let expectedTotal: number | undefined = opts.expectedTotal;

  if (!opts.bypassCache) {
    const cached = await readPtokenCache(site.id, gid);
    if (cached && cached.entries.length > 0) {
      // A cached row is trusted only when it was written as complete
      // (`total === entries.length`) AND it covers any caller-supplied
      // `expectedTotal`. Partial cache rows (`total` undefined) fall
      // through and trigger a fresh resolution — this is what protects us
      // from a previously-written 20-of-29 row sticking around.
      const cacheTotal = cached.total;
      const cacheIsAuthoritativelyComplete =
        cacheTotal !== undefined && cached.entries.length >= cacheTotal;
      const meetsExpectedTotal =
        expectedTotal === undefined || cached.entries.length >= expectedTotal;
      if (cacheIsAuthoritativelyComplete && meetsExpectedTotal) {
        const result: PtokenSet = { entries: cached.entries, complete: true, source: 'cache' };
        if (cacheTotal !== undefined) result.total = cacheTotal;
        return result;
      }
    }
  }

  // MPV fallback. If the user lacks the `q` perk, the server returns a 403
  // or an HTML stub without `imagelist` — we catch and try previews.
  //
  // MPV is taken as authoritative when it returns entries and either
  // `expectedTotal` is unknown (we have no better number to compare
  // against) or its set covers `expectedTotal`. An MPV scrape that comes
  // back shorter than `expectedTotal` would be a partial truncation; we
  // fall through to the preview loop in that rare case rather than
  // caching a too-small set.
  if (opts.mpv !== false) {
    try {
      const url = withNw(mpvUrl(site, gid, galleryToken), site);
      const text = await fetchEhText(url, site, { cookies: opts.cookies ?? '' });
      const entries = parseMpvPtokens(text);
      const mpvSufficient =
        entries.length > 0 && (expectedTotal === undefined || entries.length >= expectedTotal);
      if (mpvSufficient) {
        const total = entries.length;
        await writePtokenCache(site.id, gid, { entries, total });
        return { entries, total, complete: true, source: 'mpv' };
      }
    } catch {
      // ignore — fall through to preview pages
    }
  }

  // Preview-page fallback. Walk `?p=0`, `?p=1`, ... and collect anchors.
  //
  // The stop condition is whichever runs out first of:
  //
  //   - `maxPreviewPages` (a request-budget cap, default 20)
  //   - the parser-reported `totalPreviewPages` from `<table class="ptt">`
  //   - the count derived from `expectedTotal / thumbsPerPage` — which is
  //     the actual fix for B1: it lets us walk all required preview pages
  //     even when the pagination-footer extraction is unreliable
  //   - having collected `>= expectedTotal` entries already
  const collected = new Map<number, string>();
  const maxIterations = Math.max(1, opts.maxPreviewPages ?? 20);
  let totalPreviewPages = 1;
  let thumbsPerPage = 0;

  for (let p = 0; p < maxIterations; p++) {
    if (expectedTotal !== undefined && collected.size >= expectedTotal) break;
    if (p >= totalPreviewPages) break;
    const u = new URL(galleryUrl(site, gid, galleryToken));
    if (p > 0) u.searchParams.set('p', String(p));
    // E-Hentai treats `nw=1` as a first-preview-page override when it is
    // combined with `p=N`, so only apply it to the initial gallery page.
    const url = p === 0 ? withNw(u.toString(), site) : u.toString();
    let text: string;
    try {
      text = await fetchEhText(url, site, { cookies: opts.cookies ?? '' });
    } catch (err) {
      // If even the first preview page fails, bail with whatever we have.
      if (p === 0) throw err;
      break;
    }
    const result = parsePreviewPage(text);
    for (const [pageN, ptoken] of result.entries) collected.set(pageN, ptoken);

    // Adopt the first preview page's thumb count as the per-page size and
    // pick up `pageCount` from `#gdd` if we don't already know it. Both
    // values let us derive a robust loop bound for the next iteration.
    if (p === 0) {
      thumbsPerPage = result.entries.length;
      if (expectedTotal === undefined && result.galleryPageCount !== undefined) {
        expectedTotal = result.galleryPageCount;
      }
    }

    // Prefer the parser's pagination-footer count when present; fall back
    // to deriving from `expectedTotal / thumbsPerPage` so the loop still
    // covers all pages when the footer parse misses (the original B1
    // failure mode). We take the max so a partial signal can't *shrink*
    // the bound we've already computed.
    if (result.totalPreviewPages !== undefined) {
      totalPreviewPages = Math.max(totalPreviewPages, result.totalPreviewPages);
    }
    if (expectedTotal !== undefined && thumbsPerPage > 0) {
      const inferred = Math.ceil(expectedTotal / thumbsPerPage);
      totalPreviewPages = Math.max(totalPreviewPages, inferred);
    }
  }

  const entries = Array.from(collected.entries())
    .sort((a, b) => a[0] - b[0])
    .map<[number, string]>(([n, t]) => [n, t]);

  if (entries.length === 0) {
    throw new ParseError(
      `image-flow: no ptokens recovered for gid ${gid} (MPV + preview both empty)`,
      { source: site.id },
    );
  }

  const complete = expectedTotal === undefined || entries.length >= expectedTotal;
  await writePtokenCache(
    site.id,
    gid,
    complete ? { entries, total: expectedTotal ?? entries.length } : { entries },
  );
  const result: PtokenSet = {
    entries,
    complete,
    source: complete ? 'preview' : 'partial-preview',
  };
  if (expectedTotal !== undefined) result.total = expectedTotal;
  return result;
}

export async function getPTokenForPage(
  gid: number,
  galleryToken: string,
  page: number,
  site: SiteContext,
): Promise<string> {
  const resp = await gtoken([[gid, galleryToken, page]], site);
  const match = resp.tokenlist.find((entry) => entry.gid === gid && entry.page === page);
  if (!match?.token) {
    throw new ParseError(`gtoken: no ptoken for gid ${gid} page ${page}`, {
      source: site.id,
    });
  }
  return match.token;
}

export function primePTokenCacheFromPreviewHtml(
  gid: number | string,
  site: SiteContext,
  html: string,
): void {
  try {
    const parsed = parsePreviewPage(html);
    mergePtokenMemoryCache(site.id, gid, parsed.entries);
    if (parsed.galleryPageCount !== undefined) {
      rememberPreviewPageSize(site.id, gid, parsed.entries);
    }
    void writePtokenCache(
      site.id,
      gid,
      parsed.galleryPageCount !== undefined
        ? { entries: parsed.entries, total: parsed.galleryPageCount }
        : { entries: parsed.entries },
    );
  } catch {
    // Opportunistic only; callers still have normal preview-fetch fallback.
  }
}

async function fetchPreviewPage(
  gid: number | string,
  galleryToken: string,
  previewPage: number,
  site: SiteContext,
  opts: { cookies?: string } = {},
): Promise<PtokenSet> {
  const key = previewInFlightKey(site.id, gid, previewPage);
  const pending = previewPageInFlight.get(key);
  if (pending) return pending;

  const promise = (async () => {
    const u = new URL(galleryUrl(site, gid, galleryToken));
    if (previewPage > 0) u.searchParams.set('p', String(previewPage));
    // `nw=1` only belongs on the first preview page; with `p=N` it can force
    // e-hentai back to page 0 and hide the target page token.
    const url = previewPage === 0 ? withNw(u.toString(), site) : u.toString();
    const text = await fetchEhText(url, site, { cookies: opts.cookies ?? '' });
    const parsed = parsePreviewPage(text);
    mergePtokenMemoryCache(site.id, gid, parsed.entries);
    if (previewPage === 0) rememberPreviewPageSize(site.id, gid, parsed.entries);
    const result: PtokenSet = {
      entries: parsed.entries,
      complete:
        parsed.galleryPageCount !== undefined && parsed.entries.length >= parsed.galleryPageCount,
      source:
        parsed.galleryPageCount !== undefined && parsed.entries.length >= parsed.galleryPageCount
          ? 'preview'
          : 'partial-preview',
    };
    if (parsed.galleryPageCount !== undefined) result.total = parsed.galleryPageCount;
    return result;
  })();
  previewPageInFlight.set(key, promise);
  try {
    return await promise;
  } finally {
    if (previewPageInFlight.get(key) === promise) {
      previewPageInFlight.delete(key);
    }
  }
}

/**
 * Resolve one page's `/s/{ptoken}/{gid}-{page}` token without walking the
 * entire gallery. `api.php`'s `gtoken` method is not reliable for public
 * e-hentai galleries (it can return "Invalid page." for every page), while
 * preview anchors are the same source of truth a browser user clicks.
 */
export async function getPTokenForPageFromPreview(
  gid: number,
  galleryToken: string,
  page: number,
  site: SiteContext,
  opts: { cookies?: string } = {},
): Promise<string> {
  const key = cacheKey(site.id, gid);
  const memoryEntries = Array.from(ptokenMemoryCache.get(key)?.entries() ?? [])
    .sort((a, b) => a[0] - b[0])
    .map<[number, string]>(([n, token]) => [n, token]);
  const memoryToken = memoryEntries.find(([n]) => n === page)?.[1];
  if (memoryToken) {
    recordDiagnostic('source', 'ehentai:ptoken-memory-hit', {
      sourceId: site.id,
      gid,
      page,
      cache: ptokenCacheSummary(memoryEntries),
      previewPageSize: ptokenMemoryPreviewSizes.get(key),
    });
    return memoryToken;
  }

  const cached = await readPtokenCache(site.id, gid);
  const cachedToken = cached?.entries.find(([n]) => n === page)?.[1];
  if (cachedToken) {
    recordDiagnostic('source', 'ehentai:ptoken-idb-hit', {
      sourceId: site.id,
      gid,
      page,
      cache: ptokenCacheSummary(cached.entries, cached.total),
    });
    return cachedToken;
  }

  const cachedEntries = memoryEntries.length > 0 ? memoryEntries : (cached?.entries ?? []);
  const cachedPreviewPageSize = ptokenMemoryPreviewSizes.get(key);
  const cacheHasPreviewSize = cachedPreviewPageSize !== undefined;
  recordDiagnostic('source', 'ehentai:ptoken-cache-miss', {
    sourceId: site.id,
    gid,
    page,
    cache: ptokenCacheSummary(cachedEntries, cached?.total),
    hasPreviewPageSize: cacheHasPreviewSize,
  });
  const first = cacheHasPreviewSize
    ? {
        entries: cachedEntries.slice(0, cachedPreviewPageSize),
        complete: false,
        source: 'partial-preview' as const,
      }
    : await fetchPreviewPage(gid, galleryToken, 0, site, opts);
  mergePtokenMemoryCache(site.id, gid, first.entries);
  const collected = new Map(cachedEntries);
  for (const [n, token] of first.entries) collected.set(n, token);
  const firstToken = collected.get(page);
  if (firstToken) {
    await writePtokenCache(site.id, gid, { entries: Array.from(collected.entries()) });
    return firstToken;
  }

  const thumbsPerPage = first.entries.length;
  if (thumbsPerPage <= 0) {
    throw new ParseError(`preview: no page tokens found for gid ${gid}`, { source: site.id });
  }

  const previewPage = Math.floor((page - 1) / thumbsPerPage);
  if (previewPage > 0) {
    recordDiagnostic('source', 'ehentai:preview-fetch-target', {
      sourceId: site.id,
      gid,
      page,
      previewPage,
      thumbsPerPage,
    });
    const target = await fetchPreviewPage(gid, galleryToken, previewPage, site, opts);
    mergePtokenMemoryCache(site.id, gid, target.entries);
    for (const [n, token] of target.entries) collected.set(n, token);
    const targetToken = collected.get(page);
    const entries = Array.from(collected.entries()).sort((a, b) => a[0] - b[0]);
    await writePtokenCache(site.id, gid, { entries });
    if (targetToken) return targetToken;
  }

  throw new ParseError(`preview: no ptoken for gid ${gid} page ${page}`, {
    source: site.id,
  });
}

export interface FirstPageData {
  /** The H@H URL ready to drop into `<img src>`. */
  imageUrl: string;
  /** The "skip Hath" key, used by the user-initiated "retry image" path. */
  skipHathKey?: string;
  /** The "original image" full-resolution URL, if present. */
  originImageUrl?: string;
  /**
   * The per-gallery `showkey`. Cache this on the gallery — `showpage` for
   * page 2..N requires it.
   */
  showKey: string;
}

/**
 * Fetch `/s/{ptoken}/{gid}-1` and extract the four pieces of data this
 * page exposes (image URL, skip-hath key, original-image URL, showkey).
 *
 * Pure regex extraction below the network boundary so tests can re-use
 * the patterns directly.
 */
export async function loadFirstPage(
  gid: number | string,
  page1Ptoken: string,
  site: SiteContext,
  opts: { cookies?: string } = {},
): Promise<FirstPageData> {
  return loadPageViewer(gid, page1Ptoken, 1, site, opts);
}

export async function loadPageViewer(
  gid: number | string,
  ptoken: string,
  page1Based: number,
  site: SiteContext,
  opts: { cookies?: string } = {},
): Promise<FirstPageData> {
  const url = withNw(pageUrl(site, ptoken, gid, page1Based), site);
  const text = await fetchEhText(url, site, { cookies: opts.cookies ?? '' });
  return extractFirstPageData(text, site.id);
}

/**
 * Pure-regex helper, exposed for tests. Throws if the showkey or image URL
 * is missing.
 */
export function extractFirstPageData(html: string, source: 'ehentai' | 'exhentai'): FirstPageData {
  const imgMatch = PATTERN_IMAGE_URL.exec(html);
  if (!imgMatch || !imgMatch[1]) {
    throw new ParseError('loadFirstPage: image URL not found', { source });
  }
  if (Url509Regex.test(imgMatch[1])) {
    throw new QuotaExceeded(`509 image quota exceeded (${imgMatch[1]})`, { source });
  }
  const showKeyMatch = PATTERN_SHOW_KEY.exec(html);
  if (!showKeyMatch || !showKeyMatch[1]) {
    throw new ParseError('loadFirstPage: showkey not found', { source });
  }
  const skipHathMatch = PATTERN_SKIP_HATH_KEY.exec(html);
  const originMatch = PATTERN_ORIGIN_IMAGE_URL.exec(html);
  const data: FirstPageData = {
    imageUrl: imgMatch[1],
    showKey: showKeyMatch[1],
  };
  if (skipHathMatch?.[1]) data.skipHathKey = skipHathMatch[1];
  if (originMatch?.[1]) data.originImageUrl = originMatch[1];
  return data;
}

export interface SubsequentPageData {
  imageUrl: string;
}

/**
 * Resolve page 2..N by POSTing `showpage` to `api.php`. The response's `i3`
 * field is a JSON-escaped HTML chunk; we strip backslashes and regex out
 * the `<img>` src.
 */
export async function loadPage(
  args: { gid: number; page: number; ptoken: string; showkey: string },
  site: SiteContext,
): Promise<SubsequentPageData> {
  const payload: { gid: number; page: number; imgkey: string; showkey: string } = {
    gid: args.gid,
    page: args.page,
    imgkey: args.ptoken,
    showkey: args.showkey,
  };
  const resp = await showpage(payload, site);
  return extractShowpageImage(resp.i3 ?? '', site.id);
}

/**
 * Pure-regex helper, exposed for tests. `i3` arrives JSON-encoded; we
 * strip backslashes and pull out the `<img>` src.
 */
export function extractShowpageImage(
  i3: string,
  source: 'ehentai' | 'exhentai',
): SubsequentPageData {
  if (!i3) throw new ParseError('showpage: empty i3 chunk', { source });
  const unescaped = unescapeJsonHtml(i3);
  const m = PATTERN_SHOWPAGE_IMAGE_URL.exec(unescaped);
  if (!m || !m[1]) {
    throw new ParseError('showpage: image URL not found', { source });
  }
  if (Url509Regex.test(m[1])) {
    throw new QuotaExceeded(`509 image quota exceeded (${m[1]})`, { source });
  }
  return { imageUrl: m[1] };
}

/**
 * Build the "skip Hath" retry URL: `/s/{ptoken}/{gid}-{page}?nl={skipHathKey}`.
 * The user-triggered retry flow hits this URL through a fresh `loadFirstPage`
 * to get a different H@H node assigned.
 */
export function buildRetryUrl(
  site: SiteContext,
  args: { gid: number | string; page: number; ptoken: string; skipHathKey: string },
): string {
  const u = new URL(pageUrl(site, args.ptoken, args.gid, args.page));
  u.searchParams.set('nl', args.skipHathKey);
  return withNw(u.toString(), site);
}
